import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceArea,
} from "recharts";
import styled from "styled-components";
import { getLabDataByName, updateLabData } from "../services/labService";
import { getProfile, parseProfileForFormula } from "../services/profileService";
import MainHeader from "../Components/MainHeader";
import CustomAlert from "../Components/CustomAlert";
import { useCustomAlert } from "../hooks/useCustomAlert";
import CreatinineFormula from "../formula/Creatinine";
import EgfrFormula from "../formula/Egfr";
import BunFormula from "../formula/Bun";
import { STATUS_COLORS } from "../utils/healthStatusColors";
import {
  buildLatestCombinedRecord,
  fetchLabDataByNameFallbacks,
  getLabDisplayValue,
  getLabRawValue,
} from "../utils/labRecordUtils";

const KidneyContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  font-family: "Noto Sans Thai", sans-serif !important;
  * {
    font-family: "Noto Sans Thai", sans-serif !important;
    font-weight: normal;
    color: #666666;
  }
`;

const TopBar = styled.div`
  background-color: #00bbd3;
  padding: 16px 24px;
  color: white;
  font-size: 20px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
  text-align: left;
`;

const Header = styled.div`
  background-color: white;
  padding: 16px 24px;
  color: #666;
  font-size: 18px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const DateText = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: normal;
  margin-top: 4px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Content = styled.div`
  padding: 24px;
  max-width: 480px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: #666;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #666;
  margin: 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin: 0 0 16px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusCard = styled.div`
  background: ${(props) => props.$bgColor || "#FF5252"};
  border-radius: 16px;
  padding: 20px;
  margin-bottom: 16px;
  color: white;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const KidneyIcon = styled.img`
  width: 60px;
  height: 60px;
  flex-shrink: 0;
  object-fit: contain;
`;

const StatusInfo = styled.div`
  flex: 1;
`;

const StatusTitle = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 4px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusValue = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 2px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const StatusLabel = styled.div`
  font-size: 14px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const AbnormalItem = styled.div`
  margin-bottom: 12px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const AbnormalName = styled.div`
  font-size: 14px;
  font-weight: bold;
  color: white;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const AbnormalValue = styled.div`
  font-size: 18px;
  font-weight: bold;
  color: white;
  margin: 4px 0;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const AbnormalStatus = styled.div`
  font-size: 14px;
  color: white;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const ChartSection = styled.div`
  margin-bottom: 40px;
`;

const ChartTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Legend = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 16px;
  font-size: 14px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LegendDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
`;

const HistorySection = styled.div`
  margin-top: 40px;
  background: white;
  border-radius: 16px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const HistoryTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 16px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #00bbd3;
  color: white;
`;

const TableHeaderCell = styled.th`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  font-weight: bold;
  font-family: "Noto Sans Thai", sans-serif !important;
  color: white;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 12px 8px;
  text-align: center;
  font-size: 14px;
  color: #666;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;

  svg {
    width: 20px;
    height: 20px;
    fill: #34a853;
  }

  &:hover svg {
    fill: #45a049;
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
  font-family: "Noto Sans Thai", sans-serif !important;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  font-family: "Noto Sans Thai", sans-serif !important;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #00bbd3;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  font-family: "Noto Sans Thai", sans-serif !important;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
`;

const SaveButton = styled(Button)`
  background-color: #00bbd3;
  color: white;
`;

const CancelButton = styled(Button)`
  background-color: #e0e0e0;
  color: #666;
`;

const KidneyPage = () => {
  const navigate = useNavigate();
  const { alertState, showAlert, closeAlert } = useCustomAlert();
  const [loading, setLoading] = useState(true);
  const [creatinineData, setCreatinineData] = useState([]);
  const [bunData, setBunData] = useState([]);
  const [egfrData, setEgfrData] = useState([]);
  const [latestDate, setLatestDate] = useState("");
  const [latestValues, setLatestValues] = useState({
    creatinine: { value: "-", status: "ไม่มีข้อมูล", color: STATUS_COLORS.gray },
    egfr: { value: "-", status: "ไม่มีข้อมูล", color: STATUS_COLORS.gray },
    bun: { value: "-", status: "ไม่มีข้อมูล", color: STATUS_COLORS.gray },
  });
  const [profileData, setProfileData] = useState({ age: 0, gender: "" });
  const [allRecords, setAllRecords] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({
    bloodPressure: "",
    risk: "",
  });

  const handleBack = () => {
    navigate(-1);
  };

  const handleEditClick = (record) => {
    setEditingRecord(record);
    setEditForm({
      creatinine: record.creatinine || "",
      bun: record.bun || "",
      egfr: record.egfr || "",
    });
    setShowEditModal(true);
  };

  const handleEditSubmit = async () => {
    try {
      // Update each value if it has changed
      const updates = [];

      if (
        editForm.creatinine &&
        editForm.creatinine !== editingRecord.creatinine
      ) {
        updates.push(
          updateLabData(
            editingRecord.rawDate,
            "creatinine",
            editForm.creatinine,
            "h",
          ),
        );
      }

      if (editForm.bun && editForm.bun !== editingRecord.bun) {
        updates.push(
          updateLabData(editingRecord.rawDate, "bun", editForm.bun, "h"),
        );
      }

      if (editForm.egfr && editForm.egfr !== editingRecord.egfr) {
        updates.push(
          updateLabData(editingRecord.rawDate, "egfr", editForm.egfr, "h"),
        );
      }

      await Promise.all(updates);

      showAlert("อัปเดตข้อมูลเรียบร้อยแล้ว");

      // Reload data
      await loadKidneyData();

      // Close modal
      setShowEditModal(false);
      setEditingRecord(null);
    } catch (error) {
      showAlert("เกิดข้อผิดพลาดในการอัปเดตข้อมูล");
    }
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setEditingRecord(null);
  };

  const formatDisplayDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = (date.getFullYear() + 543).toString().slice(-2);
    return `${day}/${month}/${year}`;
  };

  const calculateRisk = (creatinine, bun, egfr) => {
    const creatinineVal = parseFloat(creatinine) || 0;
    const bunVal = parseFloat(bun) || 0;
    const egfrVal = parseFloat(egfr) || 0;

    if (!creatinine && !bun && !egfr) return "-";

    const isCreatinineAbnormal =
      creatinineVal > 0 && (creatinineVal < 0.6 || creatinineVal > 1.2);
    const isBunAbnormal = bunVal > 0 && (bunVal < 7 || bunVal > 20);
    const isEgfrAbnormal = egfrVal > 0 && egfrVal < 60;

    if (isCreatinineAbnormal || isBunAbnormal || isEgfrAbnormal) {
      return "ผิดปกติ";
    }

    return "ปกติ";
  };

  const formatThaiDate = (dateString) => {
    if (!dateString) return "";

    const thaiMonths = [
      "มกราคม",
      "กุมภาพันธ์",
      "มีนาคม",
      "เมษายน",
      "พฤษภาคม",
      "มิถุนายน",
      "กรกฎาคม",
      "สิงหาคม",
      "กันยายน",
      "ตุลาคม",
      "พฤศจิกายน",
      "ธันวาคม",
    ];

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const thaiYear = date.getFullYear() + 543;
    const thaiMonth = thaiMonths[date.getMonth()];

    return `${date.getDate()} ${thaiMonth} ${thaiYear}`;
  };

  const loadKidneyData = async () => {
    try {
      setLoading(true);

      const profileResponse = await getProfile();
      const profile = profileResponse?.data || profileResponse?.profile || {};
      const { age, gender } = parseProfileForFormula(profile);

      setProfileData({ age, gender });

      // ดึงข้อมูลจาก GetLabDataByName API โดยยิงหลาย key fallback ตามเอกสาร
      const [creatinineArray, bunArray, egfrArray] = await Promise.all([
        fetchLabDataByNameFallbacks(getLabDataByName, [
          "kidneyfunctiontestcreatinine",
          "creatinine",
          "creatinine creatinine result",
          "creatininecreatinine",
          "creatininecreatinineresult",
          "creatinine result",
          "cr",
        ]),
        fetchLabDataByNameFallbacks(getLabDataByName, [
          "kidneyfunctiontestbun",
          "bun",
          "bun result",
          "bunresult",
          "bunurea",
          "bun urea",
        ]),
        fetchLabDataByNameFallbacks(getLabDataByName, [
          "kidneyfunctiontestegfr",
          "egfr",
          "creatinine egfr result",
          "creatinine egfr",
          "creatinineegfr",
          "creatinineegfrresult",
        ]),
      ]);

      // Combine all records by date for table display
      const recordsMap = new Map();

      creatinineArray.forEach((item) => {
        const date = item.dateadd?.split("T")[0].split(" ")[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, {
            date,
            rawDate: item.dateadd,
            creatinine: null,
            bun: null,
            egfr: null,
          });
        }
        const displayValue = getLabRawValue(item);
        if (!recordsMap.get(date).creatinine && displayValue) {
          recordsMap.get(date).creatinine = displayValue;
        }
        if (!recordsMap.get(date).rawDate)
          recordsMap.get(date).rawDate = item.dateadd;
      });

      bunArray.forEach((item) => {
        const date = item.dateadd?.split("T")[0].split(" ")[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, {
            date,
            rawDate: item.dateadd,
            creatinine: null,
            bun: null,
            egfr: null,
          });
        }
        const displayValue = getLabRawValue(item);
        if (!recordsMap.get(date).bun && displayValue) {
          recordsMap.get(date).bun = displayValue;
        }
        if (!recordsMap.get(date).rawDate)
          recordsMap.get(date).rawDate = item.dateadd;
      });

      egfrArray.forEach((item) => {
        const date = item.dateadd?.split("T")[0].split(" ")[0] || item.dateadd;
        if (!recordsMap.has(date)) {
          recordsMap.set(date, {
            date,
            rawDate: item.dateadd,
            creatinine: null,
            bun: null,
            egfr: null,
          });
        }
        const displayValue = getLabRawValue(item);
        if (!recordsMap.get(date).egfr && displayValue) {
          recordsMap.get(date).egfr = displayValue;
        }
        if (!recordsMap.get(date).rawDate)
          recordsMap.get(date).rawDate = item.dateadd;
      });

      // Convert map to array and sort by date (newest first)
      const combinedRecords = Array.from(recordsMap.values()).sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );

      setAllRecords(combinedRecords);

      const sortedCombinedRecords = [...combinedRecords].sort(
        (a, b) => new Date(a.rawDate) - new Date(b.rawDate),
      );

      // Process Creatinine data for chart
      const processedCreatinine = sortedCombinedRecords.map((record) => ({
        date: record.date || record.rawDate?.split("T")[0].split(" ")[0] || "",
        value: record.creatinine != null ? parseFloat(record.creatinine) || 0 : null,
        result: record.creatinine ?? "",
        rawDate: record.rawDate,
      }));

      // Process BUN data for chart
      const processedBun = sortedCombinedRecords.map((record) => ({
        date: record.date || record.rawDate?.split("T")[0].split(" ")[0] || "",
        value: record.bun != null ? parseFloat(record.bun) || 0 : null,
        result: record.bun ?? "",
        rawDate: record.rawDate,
      }));

      // Process eGFR data for chart
      const processedEgfr = sortedCombinedRecords.map((record) => ({
        date: record.date || record.rawDate?.split("T")[0].split(" ")[0] || "",
        value: record.egfr != null ? parseFloat(record.egfr) || 0 : null,
        result: record.egfr ?? "",
        rawDate: record.rawDate,
      }));

      setCreatinineData(processedCreatinine);
      setBunData(processedBun);
      setEgfrData(processedEgfr);

      // Extract latest date from all data sources
      // ใช้ [0] เพื่อหาวันที่ล่าสุด (array sort descending แล้ว)
      let mostRecentDate = "";
      if (creatinineArray.length > 0) {
        mostRecentDate = creatinineArray[0].dateadd;
      }
      if (bunArray.length > 0 && bunArray[0].dateadd > mostRecentDate) {
        mostRecentDate = bunArray[0].dateadd;
      }
      if (egfrArray.length > 0 && egfrArray[0].dateadd > mostRecentDate) {
        mostRecentDate = egfrArray[0].dateadd;
      }

      if (mostRecentDate) {
        setLatestDate(formatThaiDate(mostRecentDate));
      }

      // Set latest values using the latest combined visit record
      const latestRecord = buildLatestCombinedRecord([
        { field: "creatinine", items: creatinineArray },
        { field: "bun", items: bunArray },
        { field: "egfr", items: egfrArray },
      ]);
      if (latestRecord) {
        const latestCreatinine =
          latestRecord.creatinine != null ? { value: latestRecord.creatinine } : null;
        const latestEgfr =
          latestRecord.egfr != null ? { value: latestRecord.egfr } : null;
        const latestBun =
          latestRecord.bun != null ? { value: latestRecord.bun } : null;

        // Calculate Creatinine status using formula
        const creatinineValue = parseFloat(latestCreatinine?.value || 0);
        const creatinineResult = latestCreatinine
          ? await CreatinineFormula(creatinineValue, age, gender)
          : null;
        const creatinineColorMap = STATUS_COLORS;

        // Calculate eGFR status using formula
        const egfrValue = parseFloat(latestEgfr?.value || 0);
        const egfrResult = latestEgfr ? EgfrFormula(egfrValue) : null;

        // Calculate BUN status using formula
        const bunValue = parseFloat(latestBun?.value || 0);
        const bunResult = latestBun
          ? await BunFormula(bunValue, age, gender)
          : null;

        // ปิด logic สีตามค่าผลตรวจ (แดง/เขียว) — บังคับให้เป็นสีเทาทั้งหมดทุกหมวด
        setLatestValues({
          creatinine: {
            value: latestCreatinine
              ? `${latestCreatinine.value} mg/dL`
              : "-",
            status: creatinineResult?.title || (latestCreatinine ? "ปกติ" : "ไม่มีข้อมูล"),
            // color: creatinineResult
            //   ? creatinineColorMap[creatinineResult.color] || STATUS_COLORS.green
            //   : latestCreatinine ? STATUS_COLORS.green : STATUS_COLORS.gray,
            color: STATUS_COLORS.gray,
            rawValue: creatinineValue,
          },
          egfr: {
            value: latestEgfr ? `${latestEgfr.value} mL/min` : "-",
            status: egfrResult?.title || (latestEgfr ? "ปกติ" : "ไม่มีข้อมูล"),
            // color: egfrResult
            //   ? creatinineColorMap[egfrResult.color] || STATUS_COLORS.green
            //   : latestEgfr ? STATUS_COLORS.green : STATUS_COLORS.gray,
            color: STATUS_COLORS.gray,
            rawValue: egfrValue,
          },
          bun: {
            value: latestBun ? `${latestBun.value} mg/dL` : "-",
            status: bunResult?.title || (latestBun ? "ปกติ" : "ไม่มีข้อมูล"),
            // color: bunResult
            //   ? creatinineColorMap[bunResult.color] || STATUS_COLORS.green
            //   : latestBun ? STATUS_COLORS.green : STATUS_COLORS.gray,
            color: STATUS_COLORS.gray,
            rawValue: bunValue,
          },
        });
      } else {
        setLatestValues({
          creatinine: { value: "-", status: "ไม่มีข้อมูล", color: STATUS_COLORS.gray },
          egfr: { value: "-", status: "ไม่มีข้อมูล", color: STATUS_COLORS.gray },
          bun: { value: "-", status: "ไม่มีข้อมูล", color: STATUS_COLORS.gray },
        });
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadKidneyData();
  }, []);

  return (
    <KidneyContainer>
      <MainHeader />

      <TopBar>ไต</TopBar>

      <Header>
        <BackButton onClick={handleBack}>&lt; ย้อนกลับ</BackButton>
        <PageTitle style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <KidneyIcon src="./ไตสี.png" alt="ไต" style={{ width: "32px", height: "32px" }} />
          ไต
        </PageTitle>
        {latestDate && (
          <DateText>บันทึกล่าสุดวันที่ {latestDate}</DateText>
        )}
      </Header>

      <Content>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px", color: "#666" }}>
            กำลังโหลดข้อมูล...
          </div>
        ) : (
          <>
            {/* Status Card — comment ทั้งการ์ดออก เพราะย้ายไอคอนไปไว้ที่ Header ด้านบนแล้ว */}
            {/* {(() => {
              // const hasData =
              //   latestValues.creatinine.value !== "-" ||
              //   latestValues.egfr.value !== "-" ||
              //   latestValues.bun.value !== "-";
              // const isCreatinineAbnormal =
              //   latestValues.creatinine.value !== "-" &&
              //   latestValues.creatinine.color !== "#34A853";
              // const isEgfrAbnormal =
              //   latestValues.egfr.value !== "-" &&
              //   latestValues.egfr.color !== "#34A853";
              // const isBunAbnormal =
              //   latestValues.bun?.value !== "-" &&
              //   latestValues.bun.color !== "#34A853";
              // const hasAbnormal =
              //   isCreatinineAbnormal || isEgfrAbnormal || isBunAbnormal;
              // let cardColor = STATUS_COLORS.gray;
              // if (hasData) {
              //   cardColor = hasAbnormal ? STATUS_COLORS.red : STATUS_COLORS.green;
              // }
              const cardColor = STATUS_COLORS.gray;

              return (
                <StatusCard $bgColor={cardColor}>
                  <KidneyIcon src="./ไตสี.png" alt="ไต" />
                  <StatusInfo>
                    <StatusTitle style={{ color: "white" }}>ไต</StatusTitle>
                    {!hasData ? (
                      <StatusTitle style={{ color: "white" }}>
                        ไม่มีข้อมูล
                      </StatusTitle>
                    ) : !hasAbnormal ? (
                      <StatusTitle style={{ color: "white" }}>
                        ผลตรวจปกติ
                      </StatusTitle>
                    ) : (
                      <>
                        {isCreatinineAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>Creatinine</AbnormalName>
                            <AbnormalValue>
                              {latestValues.creatinine.value}
                            </AbnormalValue>
                            <AbnormalStatus>
                              {latestValues.creatinine.status}
                            </AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isEgfrAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>eGFR</AbnormalName>
                            <AbnormalValue>
                              {latestValues.egfr.value}
                            </AbnormalValue>
                            <AbnormalStatus>
                              {latestValues.egfr.status}
                            </AbnormalStatus>
                          </AbnormalItem>
                        )}
                        {isBunAbnormal && (
                          <AbnormalItem>
                            <AbnormalName>BUN</AbnormalName>
                            <AbnormalValue>
                              {latestValues.bun.value}
                            </AbnormalValue>
                            <AbnormalStatus>
                              {latestValues.bun.status}
                            </AbnormalStatus>
                          </AbnormalItem>
                        )}
                      </>
                    )}
                  </StatusInfo>
                </StatusCard>
              );
            })()} */}

            {/* Creatinine Chart */}
            <ChartSection>
              <ChartTitle>Creatinine</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>ต่ำ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#C8E6C9" />
                  <span>ปกติ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>สูง</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={creatinineData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 2]} />

                  {/* ต่ำ (< 0.6) */}
                  <ReferenceArea
                    y1={0}
                    y2={0.6}
                    fill="#FFEBEE"
                    fillOpacity={0.6}
                  />
                  {/* ปกติ (0.6-1.2) */}
                  <ReferenceArea
                    y1={0.6}
                    y2={1.2}
                    fill="#E8F5E9"
                    fillOpacity={0.6}
                  />
                  {/* สูง (> 1.2) */}
                  <ReferenceArea
                    y1={1.2}
                    y2={2}
                    fill="#FFEBEE"
                    fillOpacity={0.6}
                  />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: "#2196F3", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* BUN Chart */}
            <ChartSection>
              <ChartTitle>BUN , Urea (Blood Urea Nitrogen)</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>ต่ำ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#C8E6C9" />
                  <span>ปกติ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>สูง</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={bunData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 30]} />

                  {/* ต่ำ (< 7) */}
                  <ReferenceArea
                    y1={0}
                    y2={7}
                    fill="#FFEBEE"
                    fillOpacity={0.6}
                  />
                  {/* ปกติ (7-20) */}
                  <ReferenceArea
                    y1={7}
                    y2={20}
                    fill="#E8F5E9"
                    fillOpacity={0.6}
                  />
                  {/* สูง (> 20) */}
                  <ReferenceArea
                    y1={20}
                    y2={30}
                    fill="#FFEBEE"
                    fillOpacity={0.6}
                  />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: "#2196F3", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* eGFR Chart */}
            <ChartSection>
              <ChartTitle>eGFR</ChartTitle>
              <Legend>
                <LegendItem>
                  <LegendDot $color="#FFCDD2" />
                  <span>ต่ำ</span>
                </LegendItem>
                <LegendItem>
                  <LegendDot $color="#C8E6C9" />
                  <span>ปกติ</span>
                </LegendItem>
              </Legend>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={egfrData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 120]} />

                  {/* ต่ำ (< 60) - ผิดปกติ */}
                  <ReferenceArea
                    y1={0}
                    y2={60}
                    fill="#FFEBEE"
                    fillOpacity={0.6}
                  />
                  {/* ปกติ (≥ 60) */}
                  <ReferenceArea
                    y1={60}
                    y2={120}
                    fill="#E8F5E9"
                    fillOpacity={0.6}
                  />

                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#2196F3"
                    strokeWidth={2}
                    dot={{ fill: "#2196F3", r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartSection>

            {/* History Table */}
            <HistorySection>
              <HistoryTitle>ประวัติการตรวจ</HistoryTitle>
              <Table>
                <TableHeader>
                  <tr>
                    <TableHeaderCell>วันที่ตรวจ</TableHeaderCell>
                    <TableHeaderCell>ค่าที่วัด</TableHeaderCell>
                    <TableHeaderCell>ความเสี่ยง</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                  </tr>
                </TableHeader>
                <tbody>
                  {allRecords.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan="4"
                        style={{ textAlign: "center", padding: "20px" }}
                      >
                        ไม่พบข้อมูล
                      </TableCell>
                    </TableRow>
                  ) : (
                    allRecords.map((record, index) => {
                      const values = [];
                      if (record.creatinine)
                        values.push(`Cr: ${record.creatinine}`);
                      if (record.bun) values.push(`BUN: ${record.bun}`);
                      if (record.egfr) values.push(`eGFR: ${record.egfr}`);

                      return (
                        <TableRow key={index}>
                          <TableCell>
                            {formatDisplayDate(record.date)}
                          </TableCell>
                          <TableCell>{values.join(", ") || "-"}</TableCell>
                          <TableCell>
                            {calculateRisk(
                              record.creatinine,
                              record.bun,
                              record.egfr,
                            )}
                          </TableCell>
                          <TableCell>
                            {/* ปิดการแก้ไขข้อมูลจากตารางประวัติ — comment ไอคอนแก้ไขไว้ ไม่ลบ */}
                            {/* <EditButton onClick={() => handleEditClick(record)}>
                              <svg
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                              </svg>
                            </EditButton> */}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </tbody>
              </Table>
            </HistorySection>
          </>
        )}
      </Content>

      {/* Edit Modal */}
      {showEditModal && editingRecord && (
        <Modal onClick={handleModalClose}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>แก้ไขข้อมูล</ModalTitle>

            <FormGroup>
              <Label>วันที่: {formatDisplayDate(editingRecord.date)}</Label>
            </FormGroup>

            <FormGroup>
              <Label>Creatinine (mg/dL)</Label>
              <Input
                type="text"
                value={editForm.creatinine}
                onChange={(e) =>
                  setEditForm({ ...editForm, creatinine: e.target.value })
                }
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>BUN (mg/dL)</Label>
              <Input
                type="text"
                value={editForm.bun}
                onChange={(e) =>
                  setEditForm({ ...editForm, bun: e.target.value })
                }
                placeholder=""
              />
            </FormGroup>

            <FormGroup>
              <Label>eGFR (mL/min)</Label>
              <Input
                type="text"
                value={editForm.egfr}
                onChange={(e) =>
                  setEditForm({ ...editForm, egfr: e.target.value })
                }
                placeholder=""
              />
            </FormGroup>

            <ButtonGroup>
              <CancelButton onClick={handleModalClose}>ยกเลิก</CancelButton>
              <SaveButton onClick={handleEditSubmit}>บันทึก</SaveButton>
            </ButtonGroup>
          </ModalContent>
        </Modal>
      )}

      {alertState.isOpen && (
        <CustomAlert message={alertState.message} onClose={closeAlert} />
      )}
    </KidneyContainer>
  );
};

export default KidneyPage;
